import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiMail } from "react-icons/fi";
import VerifyCodeForm from "./VerifyCodeForm";

/**
 * Standalone /verify-email page (the route a user lands on to confirm their
 * email during signup). Reads the target email from the `?email=` query param,
 * renders the shared VerifyCodeForm to collect the 6-digit code, then shows a
 * brief "Email verified" success state before redirecting to signup stage 2.
 * If no email is present in the URL, shows a "start from signup" fallback.
 *
 * Takes no props — all state comes from the URL and local component state.
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = (searchParams.get("email") || "").trim();
  const [verified, setVerified] = useState(false);

  const goToStage2 = () => navigate("/signup?stage=2", { replace: true });

  const handleVerified = () => {
    setVerified(true);
    // Backend set pending_client_id; show the success state briefly before redirecting.
    setTimeout(goToStage2, 650);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors duration-500">
      <main className="mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-3xl border border-slate-100 bg-white/90 p-10 shadow-2xl shadow-primary-200/20 backdrop-blur-sm">
          {verified ? (
            <div className="text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center bg-primary-500/10">
                <FiCheckCircle className="text-primary-600" size={28} />
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Email verified</h1>
              <p className="text-slate-600">Redirecting you to finish signup and onboarding…</p>
              <div className="mt-8">
                <button
                  type="button"
                  onClick={goToStage2}
                  className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-3 text-white font-semibold hover:bg-primary-700 transition-colors"
                >
                  Continue to onboarding
                </button>
              </div>
            </div>
          ) : email ? (
            <VerifyCodeForm email={email} onVerified={handleVerified} />
          ) : (
            <div className="text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center bg-primary-500/10">
                <FiMail className="text-primary-600" size={28} />
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Verification needed</h1>
              <p className="text-slate-600">
                Please start from the signup screen so we know which email to verify.
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-5 py-3 text-slate-800 font-semibold hover:bg-slate-50 transition-colors"
                >
                  Back to signup
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
